import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SyncHealthWidget } from '@/components/dashboard/SyncHealthWidget';
import { SyncStatusBadge } from '@/components/partner-accounts/SyncStatusBadge';
import { ConnectionStatusBadge } from '@/components/partner-accounts/ConnectionStatusBadge';
import { SyncComparisonBadge } from '@/components/partner-accounts/SyncComparisonBadge';
import { usePartnerAccounts } from '@/api/partner-accounts';
import type { PartnerAccount } from '@/types/api';

function AccountStatusCard({ account }: { account: PartnerAccount }): JSX.Element {
  return (
    <Link to={`/partner-accounts/${account.id}`}>
      <Card className="transition-colors hover:bg-muted/50" data-testid={`dashboard-account-${account.id}`}>
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium">{account.name}</h3>
                <p className="text-xs text-muted-foreground">{account.partnerType?.name ?? 'Unknown'}</p>
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="flex items-center gap-2">
              <ConnectionStatusBadge status={account.connectionStatus} />
              <SyncStatusBadge status={account.syncStatus} />
            </div>

            {account.locationCounts && (
              <div className="flex items-center gap-4 text-sm">
                <SyncComparisonBadge
                  syncedCount={account.locationCounts.total}
                  sourceCount={account.sourceListingCount}
                  sourceCountUpdatedAt={account.sourceCountUpdatedAt}
                />
                <div>
                  <span className="font-medium">{String(account.locationCounts.published)}</span>
                  <span className="text-muted-foreground ml-1">published</span>
                </div>
              </div>
            )}

            {account.lastSyncAt && (
              <p className="text-xs text-muted-foreground">
                Last sync: {new Date(account.lastSyncAt).toLocaleString()}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function AccountCardsSkeleton(): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="space-y-3">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-20" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-16" />
              </div>
              <Skeleton className="h-4 w-40" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function DashboardPage(): JSX.Element {
  const { data: accounts, isLoading, error } = usePartnerAccounts({ includeCounts: true });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Partner Bridge Dashboard</h1>
        <p className="text-muted-foreground">
          Monitor and manage your property management platform integrations.
        </p>
      </div>

      {/* Health Widget */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-40" />
              <div className="grid grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
              <Skeleton className="h-24" />
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-destructive">Failed to load dashboard data.</p>
          </CardContent>
        </Card>
      ) : (
        <SyncHealthWidget accounts={accounts ?? []} />
      )}

      {/* Partner Accounts Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Partner Accounts</h2>
          <Link to="/partner-accounts" className="text-sm text-primary hover:underline">
            View all
          </Link>
        </div>

        {isLoading ? (
          <AccountCardsSkeleton />
        ) : error ? (
          <p className="text-sm text-muted-foreground">Failed to load accounts.</p>
        ) : accounts && accounts.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {accounts.map((account) => (
              <AccountStatusCard key={account.id} account={account} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground mb-2">No partner accounts connected yet.</p>
              <Link to="/partner-accounts" className="text-sm text-primary hover:underline">
                Connect your first account
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Links */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/synced-locations">
          <Card className="transition-colors hover:bg-muted/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Synced Locations</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {accounts?.reduce((sum, a) => sum + (a.locationCounts?.total ?? 0), 0) ?? 0}
              </p>
              <p className="text-xs text-muted-foreground">Total locations synced</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/synced-locations?enrichmentStatus=pending">
          <Card className="transition-colors hover:bg-muted/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Pending Enrichment</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-amber-600">
                {accounts?.reduce((sum, a) =>
                  sum + ((a.locationCounts?.total ?? 0) - (a.locationCounts?.enriched ?? 0)), 0
                ) ?? 0}
              </p>
              <p className="text-xs text-muted-foreground">Locations need enrichment</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/duplicates">
          <Card className="transition-colors hover:bg-muted/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Duplicates</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">-</p>
              <p className="text-xs text-muted-foreground">Potential duplicates</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/booking-push-logs">
          <Card className="transition-colors hover:bg-muted/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Booking Pushes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">-</p>
              <p className="text-xs text-muted-foreground">Recent booking syncs</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
